import type { ReactNode } from 'react';
import type { TenantStatus } from '@yourstore/domain';
import { Panel } from '../../kit';
import { stageMove, stageName } from '../../../vocabulary';
import { useLocale, useT } from '../../../messages';
import { whereOf } from '../BusinessHeader';

/**
 * **Business details** — the facts it was created with, in words (console
 * redesign B2; the successor of phase A's `BusinessFacts`, itself the facts
 * half of the old Lifecycle card).
 *
 * What changed is how each fact is said. The time zone is its city and
 * abbreviation (the header's `whereOf`), with the zone id on hover for anyone
 * matching a log line; the currency is its name; the stage is its name and
 * what it means. The derived `authority` no longer headlines as a wire token:
 * every stage's meaning already says it in words ("Partners are told times are
 * approximate"), so a separate row would say it twice. The status token and
 * `authority` are one hover away on the stage, for the operator comparing this
 * page with the CLI.
 *
 * There is no Edit here, although the mockup draws one: `admin-api` has no
 * route that renames a business or moves its time zone or currency, and a
 * button that could only fail would be worse than none. Those are CLI changes
 * today.
 */
export function BusinessDetails({
  name,
  timezone,
  currency,
  plan,
  status,
  authority,
  catalogVersion,
}: {
  name: string;
  timezone: string;
  currency: string;
  plan: string;
  status: TenantStatus;
  authority: 'live' | 'approximate';
  catalogVersion: number | null;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const meaning = stageMove(t, status, name)?.meaning ?? '';

  return (
    <Panel title={t('business.details.title')}>
      <dl className="kv">
        <dt>{t('common.name')}</dt>
        <dd>{name}</dd>
        <dt>{t('setup.details.stage')}</dt>
        <dd title={`${status} · ${authority}`}>
          <strong>{stageName(t, status)}</strong>
          <span className="faint">
            {' — '}
            {meaning === ''
              ? authority === 'live'
                ? t('setup.details.exact')
                : t('setup.details.approximate')
              : meaning}
          </span>
        </dd>
        <dt>{t('setup.details.timezone')}</dt>
        <dd title={timezone}>{whereOf(timezone)}</dd>
        <dt>{t('setup.details.currency')}</dt>
        <dd title={currency}>{currencyName(locale, currency)}</dd>
        <dt>{t('common.plan')}</dt>
        <dd>{plan.charAt(0).toUpperCase() + plan.slice(1)}</dd>
        <dt>{t('setup.details.catalog')}</dt>
        <dd>
          {catalogVersion === null
            ? t('setup.details.noCatalog')
            : t('setup.details.catalogNote', { n: catalogVersion })}
        </dd>
      </dl>
    </Panel>
  );
}

/** "US Dollar" for `USD`; the code itself if the runtime has no name for it. */
function currencyName(locale: string, code: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
}
